import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GLOBAL_DATA_KEY, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { Mode, OfficeType, ROUTE_PATH } from '@core/constants';
import { buildBlockUnitDropdown, buildGroupDropdown } from '@modules/check-sheet/helper/check-sheet-data.helper';
import { CheckSheetService } from '@core/services/apis';
import { AuthService } from '@core/services/apis/auth.service';
import { OfficeService } from '@core/services/apis/office.service';
import { BehaviorSubject, catchError, lastValueFrom, of, timeout } from 'rxjs';
import { GlobalDataService } from './global-data.service';
import { LocalStorageService } from './local-storage.service';
import { SessionContextService } from './session-context.service';
import { SessionStorageService } from './session-storage.service';
import { TvLoginSessionService } from './tv-login-session.service';
import { FirebaseDeviceInfoService } from './firebase/firebase-device-info.service';
import { DeviceLicensesService } from '@core/services/apis/device-licenses.service';
import { NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';

@Injectable({
  providedIn: 'root'
})
export class TvStartupService {
  private readonly statusMessageSubject = new BehaviorSubject<string>('ログイン状態を確認しています');
  readonly statusMessage$ = this.statusMessageSubject.asObservable();
  private readonly masterPreloadTimeoutMs = 15000;
  private startupCompleted = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly localStorageService: LocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly sessionContextService: SessionContextService,
    private readonly tvLoginSessionService: TvLoginSessionService,
    private readonly globalDataService: GlobalDataService,
    private readonly officeService: OfficeService,
    private readonly checkSheetService: CheckSheetService,
    private readonly firebaseDeviceInfoService: FirebaseDeviceInfoService,
    private readonly deviceLicensesService: DeviceLicensesService,
    private readonly nativeDiagnosticLogService: NativeDiagnosticLogService
  ) { }

  async navigateToCurrentStep(): Promise<boolean> {
    this.log('起動ステップを確認しています');
    const route = this.getRedirectRoute(this.router.url);
    if (!route) {
      this.log('TV起動フローは不要です');
      return false;
    }

    if (route === ROUTE_PATH.TV_BLANK) {
      // Only the first startup of this page can follow a reload (later ones come from logout / re-login).
      if (!this.startupCompleted) this.saveReloadScreen();
      // Mark this page as web 1.17.0+ (set again after a re-login, as logout clears the session storage).
      this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.sinceWebVer1170, true);
      await this.preloadTvSessionData();
      this.startupCompleted = true;
    }

    const destination = this.getStartupDestination(route);
    this.log(this.getNavigationMessage(destination));
    return this.router.navigateByUrl(destination, { replaceUrl: true });
  }

  getRedirectRoute(url: string): string | null {
    if (!this.isTvStartup(url)) {
      return null;
    }
    this.loadLogEnabled();
    this.log('保存済みログイン情報を確認しています');
    this.tvLoginSessionService.restoreSession();
    const route = this.getCurrentStepRoute();
    const currentUrl = (url ?? '').replace(/^\/+/, '').split('?')[0];

    return currentUrl === route ? null : route;
  }

  getCurrentStepRoute(): string {
    const hasCustLogin = this.hasCustLogin();
    const hasValidStaffSession = this.hasValidStaffSession();
    const hasSelectedOffice = this.hasSelectedOffice();

    if (!hasCustLogin) return ROUTE_PATH.CUST_LOGIN;
    if (!hasValidStaffSession) return ROUTE_PATH.LOGIN;
    if (!hasSelectedOffice) return ROUTE_PATH.TV_OFFICE_SELECTION;
    return ROUTE_PATH.TV_BLANK;
  }

  isStartupCompleted(): boolean {
    return this.startupCompleted;
  }

  isStartupProtectedRoute(url: string): boolean {
    const route = (url ?? '').replace(/^\/+/, '').split('?')[0];
    return route === ROUTE_PATH.TV_BLANK
      || route === ROUTE_PATH.TV_MENU_SHOW
      || route === ROUTE_PATH.TV_MENU_SHOW_YB;
  }

  getStartupUrl(returnUrl: string): string {
    return `/${ROUTE_PATH.TV_STARTUP}?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  private isTvStartup(url: string = this.router.url): boolean {
    const mode = this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode);
    const hasTvSession = !!this.localStorageService.getItem(LOCAL_STORAGE_KEY.tvLoginSession);
    const route = (url ?? '').replace(/^\/+/, '').split('?')[0];
    const isTvRoute = route.startsWith('tv/');
    const isTvStartup = mode === Mode.TV || hasTvSession || isTvRoute;
    return isTvStartup;
  }

  private hasCustLogin(): boolean {
    return !!this.localStorageService.getItem(LOCAL_STORAGE_KEY.enCorpPrefix)
      || this.hasValidStaffSession();
  }

  private hasValidStaffSession(): boolean {
    return this.authService.loggedIn();
  }

  private hasSelectedOffice(): boolean {
    return !!this.sessionContextService.getOfficeSelected();
  }

  private getStartupDestination(defaultRoute: string): string {
    if (defaultRoute !== ROUTE_PATH.TV_BLANK) return defaultRoute;

    const returnUrl = this.router.parseUrl(this.router.url).queryParams?.['returnUrl'];
    if (typeof returnUrl !== 'string') return defaultRoute;

    return this.isStartupProtectedRoute(returnUrl) ? returnUrl : defaultRoute;
  }

  // A remote Reload saves the screen to reopen (FirebaseDeviceSessionService). A TV still running web older than 1.17.0
  // cannot, so for its first reload fall back to the URL kept by the reload: returnUrl is the screen open before it.
  // A cold start (app launch) is a normal navigation, so it never reopens a screen.
  private saveReloadScreen(): void {
    // Set by the page before this reload when it ran web 1.17.0+.
    if (this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.sinceWebVer1170)) return;

    const returnUrl = this.router.parseUrl(this.router.url).queryParams?.['returnUrl'];
    const path = typeof returnUrl === 'string' ? returnUrl.split('?')[0] : '';
    if (!this.isPageReload() || !path.startsWith('/tv/') || this.isStartupProtectedRoute(path)) return;

    this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.remoteReloadScreen, {
      screen: this.localStorageService.getItem(LOCAL_STORAGE_KEY.screen),
      path
    });
  }

  private isPageReload(): boolean {
    const navigation = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
    // Older WebViews without Navigation Timing Level 2 only have the deprecated performance.navigation.
    return navigation ? navigation.type === 'reload' : performance.navigation?.type === 1;
  }

  private async preloadTvSessionData(): Promise<void> {
    this.log('表示データを準備しています');
    this.globalDataService.setGlobalData();
    void this.syncVersionAfterReload();

    await this.ensureDaiInfo();
    await Promise.all([
      this.ensureBlockAndUnitData(),
      this.ensureGroupData(),
      this.ensureValidSchedulePeriod()
    ]);
  }

  // after the clear cache button reloads the web, push the new web version to DynamoDB
  private async syncVersionAfterReload(): Promise<void> {
    if (!this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.syncVersionAfterReload)) return;
    this.sessionStorageService.removeSessionData(SESSION_STORAGE_KEY.syncVersionAfterReload);
    await this.deviceLicensesService.syncDeviceMetadata(true);
  }

  private async ensureDaiInfo(): Promise<void> {
    const daiInfo = this.sessionStorageService.getSessionData<any[]>(SESSION_STORAGE_KEY.daiInfo) || [];
    if (daiInfo.length > 0) return;

    this.log('利用者情報を準備しています');
    const officeSelected = this.sessionContextService.getOfficeSelected<any>();
    const officeCd = officeSelected?.officeCd;
    if (!officeCd) throw new Error('TV startup missing selected office');

    const officeType = officeSelected?.officeType ?? OfficeType.newTsusho;
    const res: any = await this.getNullableMasterResponse(this.officeService.getDaiInfo(officeCd, officeType));
    const result = res?.status === 200 && Array.isArray(res?.result) ? res.result : [];
    this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.daiInfo, result);
    this.globalDataService.set(GLOBAL_DATA_KEY.userInfo, result);
    // NOTE: no avatar request here on purpose. TV startup lands on the
    // digital-signage menu screen (no user avatars); the only TV screen that
    // shows avatars (user-dashboard-tv) requests them itself per its own
    // lifecycle. Prefetching every avatar at startup would just re-introduce
    // the bulk fetch this change set out to remove.
  }

  private async ensureBlockAndUnitData(): Promise<void> {
    const blockAndUnitData = this.sessionStorageService.getSessionData<any[]>(LOCAL_STORAGE_KEY.blockAndUnitData) || [];
    if (blockAndUnitData.length > 0) return;

    this.log('表示グループを準備しています');
    const res: any = await this.getNullableMasterResponse(this.officeService.getBlockAndUnitInfo());
    const resData = res?.status === 200 && res?.result?.isSuccessStatusCode
      ? this.parseMasterJson(res.result.json)
      : null;
    this.sessionStorageService.setSessionData(LOCAL_STORAGE_KEY.blockAndUnitData, buildBlockUnitDropdown(resData));
  }

  private async ensureGroupData(): Promise<void> {
    const groupData = this.sessionStorageService.getSessionData<any[]>(LOCAL_STORAGE_KEY.groupData) || [];
    if (groupData.length > 0) return;

    const res: any = await this.getNullableMasterResponse(this.officeService.getGroupInfo());
    const resData = res?.status === 200 && res?.result?.isSuccessStatusCode
      ? this.parseMasterJson(res.result.json)
      : null;
    this.sessionStorageService.setSessionData(LOCAL_STORAGE_KEY.groupData, buildGroupDropdown(resData));
  }

  private async getNullableMasterResponse(source$: any): Promise<any> {
    return lastValueFrom(source$.pipe(
      timeout({ first: this.masterPreloadTimeoutMs }),
      catchError(() => of(null))
    ));
  }

  private parseMasterJson(json: string): any {
    try {
      return json ? JSON.parse(json) : null;
    } catch {
      return null;
    }
  }

  private async ensureValidSchedulePeriod(): Promise<void> {
    const validSchedulePeriod = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.validSchedulePeriod);
    if (validSchedulePeriod) return;

    const daiInfo = this.sessionStorageService.getSessionData<any[]>(SESSION_STORAGE_KEY.daiInfo) || [];
    const daiCds = daiInfo.map((dai: any) => dai.daicd).filter((daicd: string) => !!daicd);
    if (daiCds.length === 0) return;

    this.log('予定情報を準備しています');
    const res = await this.getNullableMasterResponse(this.checkSheetService.getValidSchedulePeriod(daiCds, this.getJapanAnchorDate()));
    if (res?.status === 200 && res?.result) {
      this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.validSchedulePeriod, res.result);
    }
  }

  private getJapanAnchorDate(): string {
    const now = new Date();
    const jpTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
    const year = jpTime.getFullYear();
    const month = String(jpTime.getMonth() + 1).padStart(2, '0');
    const day = String(jpTime.getDate()).padStart(2, '0');
    const hours = String(jpTime.getHours()).padStart(2, '0');
    const minutes = String(jpTime.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  private getNavigationMessage(route: string): string {
    switch (route) {
      case ROUTE_PATH.CUST_LOGIN:
        return 'お客様ログインへ移動します';
      case ROUTE_PATH.LOGIN:
        return 'スタッフログインへ移動します';
      case ROUTE_PATH.TV_OFFICE_SELECTION:
        return '事業所選択へ移動します';
      case ROUTE_PATH.TV_BLANK:
        return 'TVメニューへ移動します';
      default:
        return '次の画面へ移動します';
    }
  }

  private log(message: string): void {
    this.statusMessageSubject.next(message);
    console.info(`[TV Startup] ${message}`);
  }

  // Fire-and-forget: fetched before login since the device ID is available without a staff session.
  private loadLogEnabled(): void {
    const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
    if (!deviceId) return;

    this.deviceLicensesService.getLogEnabledByDeviceId(deviceId).then((logEnabled) => {
      this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
    });
  }
}
